/**
 * Verify ledger entries in the browser, without trusting the server.
 *
 * The platform signs every ledger entry with Ed25519 over a SHA-256 of a
 * canonical JSON encoding of a fixed field tuple. All three of those are
 * available in the browser through WebCrypto, so a signature can be checked
 * on the reader's own machine with nothing but the public key — which the
 * API publishes deliberately unauthenticated, because a verification key only
 * its issuer can fetch verifies nothing.
 *
 * Why this is worth the trouble: `GET /ledger/verify` asks the server whether
 * its own records are intact. That answer is only as good as the server. A
 * check that runs here, over bytes the reader can see, is evidence of a
 * different kind. It is also the demo: "verify it yourself" has to mean
 * exactly that.
 *
 * The hard part is not the cryptography, it is matching Python's
 * `json.dumps(obj, sort_keys=True, separators=(",", ":"))` byte for byte.
 * Python's default `ensure_ascii=True` escapes every character outside
 * printable ASCII as `\uXXXX` (lowercase hex, surrogate pairs for astral
 * code points), where `JSON.stringify` would emit the character itself. A
 * rationale containing a degree sign or an em dash would therefore hash
 * differently under a naive port and report tampering that is really an
 * encoding mismatch. `canonicalJson` below reproduces Python's rules. The
 * signed fields are strings, integers and nulls only — no floats, whose
 * formatting genuinely differs between the two languages — and a test in
 * this repo pins the encoder against vectors generated by the Python side.
 */

import type { LedgerEvent } from "../api";

/**
 * Field tuples by signature version. These mirror `_SIGNED_FIELDS_V1` and
 * `_SIGNED_FIELDS_V2` in platform-api/ledger.py and must never be edited in
 * place: every historical entry was hashed over exactly these.
 */
const SIGNED_FIELDS_V1 = [
  "id",
  "project_id",
  "seq",
  "channel",
  "event_type",
  "actor",
  "action",
  "rationale",
  "destination",
  "payload_bytes",
  "created_at",
  "prev_hash",
] as const;

const SIGNED_FIELDS_V2 = [...SIGNED_FIELDS_V1, "payload_digest", "sig_version"] as const;

const FIELDS_BY_VERSION: Record<number, readonly string[]> = {
  1: SIGNED_FIELDS_V1,
  2: SIGNED_FIELDS_V2,
};

export const GENESIS_HASH = "0".repeat(64);

type Signable = Record<string, unknown>;

export function signedFieldsFor(entry: Signable): readonly string[] {
  // Python: `entry.get("sig_version") or 1`. Absent, null and 0 all mean v1.
  const v = Number(entry.sig_version) || 1;
  return FIELDS_BY_VERSION[v] ?? SIGNED_FIELDS_V1;
}

/** Python's `json.dumps` string escaping with `ensure_ascii=True`. */
function pyEscapeString(s: string): string {
  let out = '"';
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    const ch = s[i];
    if (ch === '"') out += '\\"';
    else if (ch === "\\") out += "\\\\";
    else if (ch === "\n") out += "\\n";
    else if (ch === "\r") out += "\\r";
    else if (ch === "\t") out += "\\t";
    else if (ch === "\b") out += "\\b";
    else if (ch === "\f") out += "\\f";
    else if (code < 0x20 || code > 0x7e) {
      // Python escapes everything outside space..tilde, DEL included, and
      // walks UTF-16 code units so astral characters come out as pairs.
      out += "\\u" + code.toString(16).padStart(4, "0");
    } else out += ch;
  }
  return out + '"';
}

function pyEncodeValue(v: unknown): string {
  if (v === null || v === undefined) return "null";
  if (typeof v === "string") return pyEscapeString(v);
  if (typeof v === "number") {
    if (!Number.isInteger(v)) {
      // Never reached for the signed tuple; refusing beats guessing at
      // Python's float repr.
      throw new Error("canonicalJson: non-integer number in signed fields");
    }
    return String(v);
  }
  if (typeof v === "boolean") return v ? "true" : "false";
  throw new Error(`canonicalJson: unsupported value type ${typeof v}`);
}

/**
 * The exact bytes Python hashed: signed fields only, keys sorted, no
 * whitespace, ASCII-escaped strings.
 */
export function canonicalJson(entry: Signable): string {
  const keys = [...signedFieldsFor(entry)].sort();
  const parts = keys.map(
    (k) => pyEscapeString(k) + ":" + pyEncodeValue(entry[k] ?? null),
  );
  return "{" + parts.join(",") + "}";
}

// WebCrypto wants BufferSource backed by a plain ArrayBuffer. Building the
// views over an explicit ArrayBuffer keeps the types honest without casts.
function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  if (hex.length % 2 !== 0 || /[^0-9a-fA-F]/.test(hex)) {
    throw new Error("malformed hex");
  }
  const out = new Uint8Array(new ArrayBuffer(hex.length / 2));
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function utf8Bytes(text: string): Uint8Array<ArrayBuffer> {
  const encoded = new TextEncoder().encode(text);
  const out = new Uint8Array(new ArrayBuffer(encoded.byteLength));
  out.set(encoded);
  return out;
}

function bytesToHex(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (const b of arr) s += b.toString(16).padStart(2, "0");
  return s;
}

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", utf8Bytes(text));
  return bytesToHex(digest);
}

let ed25519Support: Promise<boolean> | null = null;

/**
 * Whether this browser can verify Ed25519 at all. Reported rather than
 * assumed: a page that claims "verified" on a browser that silently could
 * not check anything would be the worst version of this feature.
 */
export function browserSupportsEd25519(): Promise<boolean> {
  if (!ed25519Support) {
    ed25519Support = (async () => {
      try {
        if (!globalThis.crypto?.subtle) return false;
        await crypto.subtle.importKey(
          "raw",
          new Uint8Array(new ArrayBuffer(32)),
          { name: "Ed25519" },
          false,
          ["verify"],
        );
        return true;
      } catch {
        return false;
      }
    })();
  }
  return ed25519Support;
}

export interface EntryVerification {
  /** Recomputed hash over the canonical bytes equals `entry_hash`. */
  hashOk: boolean;
  /** Ed25519 signature over the raw hash bytes verifies against the key. */
  signatureOk: boolean;
  /** False when this browser cannot verify Ed25519; both flags are then false. */
  supported: boolean;
  computedHash: string;
  canonical: string;
  problems: string[];
}

/**
 * Verify one entry against a hex-encoded Ed25519 public key.
 *
 * Mirrors `verify_entry` in platform-api/ledger.py: recompute the hash, then
 * check the signature over `bytes.fromhex(entry_hash)` — the 32 raw hash
 * bytes, not the hex string.
 */
export async function verifyEntry(
  entry: LedgerEvent,
  publicKeyHex: string,
): Promise<EntryVerification> {
  const problems: string[] = [];
  const canonical = canonicalJson(entry as unknown as Signable);
  const computedHash = await sha256Hex(canonical);
  const hashOk = computedHash === entry.entry_hash;
  if (!hashOk) {
    problems.push(`seq ${entry.seq}: entry_hash mismatch`);
  }

  const supported = await browserSupportsEd25519();
  if (!supported) {
    problems.push("this browser cannot verify Ed25519 signatures");
    return { hashOk, signatureOk: false, supported, computedHash, canonical, problems };
  }

  let signatureOk = false;
  try {
    const key = await crypto.subtle.importKey(
      "raw",
      hexToBytes(publicKeyHex),
      { name: "Ed25519" },
      false,
      ["verify"],
    );
    signatureOk = await crypto.subtle.verify(
      { name: "Ed25519" },
      key,
      hexToBytes(entry.signature),
      hexToBytes(entry.entry_hash),
    );
  } catch (e) {
    problems.push(`seq ${entry.seq}: ${(e as Error).message}`);
  }
  if (!signatureOk && supported) {
    problems.push(`seq ${entry.seq}: signature invalid`);
  }
  return { hashOk, signatureOk, supported, computedHash, canonical, problems };
}

export interface ChainCheck {
  /** Entries in ascending seq order, each with its verification. */
  entries: Array<{ entry: LedgerEvent; result: EntryVerification; linkOk: boolean }>;
  allOk: boolean;
  supported: boolean;
  problems: string[];
}

/**
 * Verify a run of entries and the links between them.
 *
 * `linkOk` is true when an entry's `prev_hash` equals the previous entry's
 * `entry_hash`. For a filtered subset (one deployment's entries out of a
 * project chain) the links are usually *not* contiguous, so callers decide
 * whether that matters; here it is reported, not judged.
 */
export async function verifyChain(
  events: LedgerEvent[],
  publicKeyHex: string,
): Promise<ChainCheck> {
  const ordered = [...events].sort((a, b) => a.seq - b.seq);
  const out: ChainCheck["entries"] = [];
  const problems: string[] = [];
  let prevHash: string | null = null;
  let supported = true;

  for (const entry of ordered) {
    const result = await verifyEntry(entry, publicKeyHex);
    supported = supported && result.supported;
    const linkOk = prevHash === null ? true : entry.prev_hash === prevHash;
    out.push({ entry, result, linkOk });
    problems.push(...result.problems);
    prevHash = entry.entry_hash;
  }

  return {
    entries: out,
    allOk: out.length > 0 && out.every((e) => e.result.hashOk && e.result.signatureOk),
    supported,
    problems,
  };
}
